import React, { useCallback, useEffect, useState } from "react";
import ReactPaginate from "react-paginate";
import Axios from "../../services/AxiosInstance";

const DEFAULT_PER_PAGE = Number(process.env.REACT_APP_PER_PAGE) || 10;

// HR Actions shows Partner Portal HR audit rows only (server-enforced).
const HR_PORTAL = "partner";

const formatDateTime = (value) => {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleString();
};

const HrActions = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(DEFAULT_PER_PAGE);
  const [totalPages, setTotalPages] = useState(0);
  const [total, setTotal] = useState(0);

  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [performedBy, setPerformedBy] = useState("");
  const [appliedPerformedBy, setAppliedPerformedBy] = useState("");
  const [moduleFilter, setModuleFilter] = useState("");
  const [actionFilter, setActionFilter] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const [modules, setModules] = useState([]);
  const [actions, setActions] = useState([]);

  const fetchFilterOptions = useCallback(async () => {
    try {
      const res = await Axios.get(
        `api/activities/filter-options?portal=${HR_PORTAL}`
      );
      const data = res.data?.data || {};
      setModules(data.modules || []);
      setActions(data.actions || []);
    } catch (err) {
      // Dropdowns stay as-is; the listing is unaffected.
    }
  }, []);

  useEffect(() => {
    fetchFilterOptions();
  }, [fetchFilterOptions]);

  const fetchActions = useCallback(
    async (pageNo) => {
      setLoading(true);
      setError("");
      setNotice("");
      try {
        const params = new URLSearchParams({
          pageNo: String(pageNo),
          perPage: String(perPage),
          portal: HR_PORTAL,
        });
        if (appliedSearch) params.set("search", appliedSearch);
        if (appliedPerformedBy) params.set("performedBy", appliedPerformedBy);
        if (moduleFilter) params.set("module", moduleFilter);
        if (actionFilter) params.set("action", actionFilter);
        if (fromDate) params.set("from_date", fromDate);
        if (toDate) params.set("to_date", toDate);

        const res = await Axios.get(`api/activities?${params.toString()}`);
        const payload = res.data?.data || {};
        const list = Array.isArray(payload)
          ? payload
          : payload.activities || [];
        const totalCount = Array.isArray(payload)
          ? payload.length
          : payload.total || 0;
        const pages = Array.isArray(payload)
          ? 1
          : payload.total_pages || 0;
        setItems(list);
        setTotal(totalCount);
        setTotalPages(pages);
        setPage(Array.isArray(payload) ? 1 : payload.page || pageNo);
      } catch (err) {
        setError(
          err?.response?.data?.message || "Failed to load actions. Please retry."
        );
        setItems([]);
        setTotal(0);
        setTotalPages(0);
      } finally {
        setLoading(false);
      }
    },
    [
      perPage,
      appliedSearch,
      appliedPerformedBy,
      moduleFilter,
      actionFilter,
      fromDate,
      toDate,
    ]
  );

  useEffect(() => {
    fetchActions(page);
  }, [fetchActions, page]);

  const applyFilters = () => {
    setAppliedSearch(search.trim());
    setAppliedPerformedBy(performedBy.trim());
    setPage(1);
  };

  const clearFilters = () => {
    setSearch("");
    setAppliedSearch("");
    setPerformedBy("");
    setAppliedPerformedBy("");
    setModuleFilter("");
    setActionFilter("");
    setFromDate("");
    setToDate("");
    setPage(1);
  };

  const handlePageClick = (event) => {
    setPage(event.selected + 1);
  };

  // Retention delete: server removes only VedMG_HRActions docs strictly
  // older than 24 hours (cutoff fixed server-side). Cancel does nothing.
  const handleDeleteOldActions = async () => {
    if (
      !window.confirm(
        "Are you sure you want to delete all HR actions older than 24 hours?"
      )
    ) {
      return;
    }
    setDeleting(true);
    setNotice("");
    setError("");
    try {
      const res = await Axios.delete(`api/activities/old`);
      const deleted = Number(res.data?.data?.deleted) || 0;
      setNotice(
        deleted === 0
          ? "No actions older than 24 hours found. Nothing was deleted."
          : `Deleted ${deleted} action${deleted === 1 ? "" : "s"} older than 24 hours.`
      );
      // Refresh the list (back to page 1, where the newest rows live) and
      // the filter dropdowns, which may have lost values with the deletions.
      if (page === 1) {
        await fetchActions(1);
      } else {
        setPage(1);
      }
      await fetchFilterOptions();
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          "Failed to delete old actions. Please retry."
      );
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="container mt-4" style={{ maxWidth: "1280px", margin: "0 auto" }}>
      <h1
        className="text-center mb-4"
        style={{ fontWeight: "700", color: "#0B2B4C" }}
      >
        HR ACTIONS
      </h1>
      <p className="text-center text-muted mb-4">
        Centralized audit trail of important activities performed across the HR
        Portal. Newest first.
      </p>

      <div
        className="card mb-4"
        style={{
          border: "none",
          borderRadius: "15px",
          boxShadow: "0 4px 15px rgba(0,0,0,0.08)",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            background: "#0B2B4C",
            color: "#fff",
            padding: "15px 25px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "10px",
          }}
        >
          <span style={{ fontSize: "20px", fontWeight: "600" }}>
            Filter Actions
          </span>
          <span style={{ fontSize: "14px", opacity: 0.85 }}>
            {total} record{total === 1 ? "" : "s"}
          </span>
        </div>

        <div className="p-4">
          <div className="row g-3 hr-actions-filters">
            <div className="col-md-4">
              <input
                type="text"
                className="form-control"
                placeholder="Search description, entity, user..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") applyFilters();
                }}
              />
            </div>
            <div className="col-md-4">
              <input
                type="text"
                className="form-control"
                placeholder="HR / User (name, email or id)"
                value={performedBy}
                onChange={(e) => setPerformedBy(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") applyFilters();
                }}
              />
            </div>
            <div className="col-md-2">
              <select
                className="form-control"
                value={moduleFilter}
                onChange={(e) => {
                  setModuleFilter(e.target.value);
                  setPage(1);
                }}
              >
                <option value="">All Modules</option>
                {modules.map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-2">
              <select
                className="form-control"
                value={actionFilter}
                onChange={(e) => {
                  setActionFilter(e.target.value);
                  setPage(1);
                }}
              >
                <option value="">All Actions</option>
                {actions.map((a) => (
                  <option key={a} value={a}>
                    {a}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-2">
              <input
                type="date"
                className="form-control"
                value={fromDate}
                max={toDate || undefined}
                onChange={(e) => {
                  setFromDate(e.target.value);
                  setPage(1);
                }}
              />
            </div>
            <div className="col-md-2">
              <input
                type="date"
                className="form-control"
                value={toDate}
                min={fromDate || undefined}
                onChange={(e) => {
                  setToDate(e.target.value);
                  setPage(1);
                }}
              />
            </div>
            <div className="col-md-3 d-flex gap-2">
              <button className="btn btn-primary btn-compact" onClick={applyFilters}>
                Search
              </button>
              <button
                className="btn btn-secondary btn-compact"
                onClick={clearFilters}
              >
                Clear
              </button>
            </div>
            <div className="col-md-3 d-flex align-items-center gap-2">
              <label className="mb-0" htmlFor="hr-actions-per-page">
                Per page:
              </label>
              <select
                id="hr-actions-per-page"
                className="form-control"
                style={{ maxWidth: "110px" }}
                value={perPage}
                onChange={(e) => {
                  setPerPage(Number(e.target.value) || DEFAULT_PER_PAGE);
                  setPage(1);
                }}
              >
                {[10, 20, 50, 100].map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
              <button
                className="btn btn-outline-primary"
                onClick={() => fetchActions(page)}
                disabled={loading}
              >
                Refresh
              </button>
              <button
                className="btn btn-danger btn-compact"
                onClick={handleDeleteOldActions}
                disabled={loading || deleting}
                title="Delete actions older than 24 hours"
              >
                {deleting ? "Deleting..." : "Delete Old Actions"}
              </button>
            </div>
          </div>
        </div>
      </div>

      {notice && !loading && (
        <div className="alert alert-success text-center">{notice}</div>
      )}

      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <div className="mt-2 text-muted">Loading actions...</div>
        </div>
      ) : error ? (
        <div className="alert alert-danger text-center">
          {error}{" "}
          <button
            className="btn btn-sm btn-outline-danger ms-2"
            onClick={() => fetchActions(page)}
          >
            Retry
          </button>
        </div>
      ) : items.length === 0 ? (
        <div className="alert alert-info text-center">
          No actions found. Try adjusting filters or check back after HR users
          perform activities.
        </div>
      ) : (
        <div className="table-responsive">
          <table className="table table-bordered table-striped">
            <thead className="table-dark">
              <tr>
                <th style={{ minWidth: "150px" }}>Date &amp; Time</th>
                <th style={{ minWidth: "150px" }}>Performed By</th>
                <th>Module</th>
                <th>Action</th>
                <th style={{ minWidth: "240px" }}>Description</th>
                <th style={{ minWidth: "150px" }}>Entity / Record</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id}>
                  <td>{formatDateTime(item.createdAt)}</td>
                  <td>
                    <div style={{ fontWeight: 600 }}>
                      {item.performedByName || "-"}
                    </div>
                    {item.performedByEmail && (
                      <div className="text-muted" style={{ fontSize: "12px" }}>
                        {item.performedByEmail}
                      </div>
                    )}
                    {item.performedById && (
                      <div className="text-muted" style={{ fontSize: "12px" }}>
                        {item.performedById}
                      </div>
                    )}
                  </td>
                  <td>
                    <span className="badge bg-secondary">{item.module}</span>
                  </td>
                  <td>{item.action}</td>
                  <td>{item.description || item.label || "-"}</td>
                  <td>
                    {item.entityLabel || item.entityId ? (
                      <>
                        <div>{item.entityLabel || "-"}</div>
                        {item.entityId && (
                          <div
                            className="text-muted"
                            style={{ fontSize: "12px" }}
                          >
                            {item.entityType ? `${item.entityType}: ` : ""}
                            {item.entityId}
                          </div>
                        )}
                      </>
                    ) : (
                      "-"
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {totalPages > 1 && (
        <div className="d-flex flex-column align-items-center mt-3">
          <ReactPaginate
            previousLabel="Previous"
            nextLabel="Next"
            breakLabel="..."
            pageCount={totalPages}
            forcePage={Math.min(page, totalPages) - 1}
            onPageChange={handlePageClick}
            containerClassName="pagination justify-content-center"
            pageClassName="page-item"
            pageLinkClassName="page-link"
            previousClassName="page-item"
            previousLinkClassName="page-link"
            nextClassName="page-item"
            nextLinkClassName="page-link"
            breakClassName="page-item"
            breakLinkClassName="page-link"
            activeClassName="active"
            disabledClassName="disabled"
          />
          <div className="text-muted" style={{ fontSize: "14px" }}>
            Page {page} of {totalPages} ({total} records)
          </div>
        </div>
      )}
    </div>
  );
};

export default HrActions;
